import React from 'react';
import { BGP_EDGE_COLORS } from '../styles/bgpVisibilityStyles';

interface GraphLegendProps {
  /** Cluster mode: show the yellow inconsistency (count-mismatch) legend entry. */
  cluster?: boolean;
  /** Cluster mode: number of route tables whose prefix count differs between the two members. */
  inconsistentCount?: number;
}

const LegendRow: React.FC<{ color: string; label: string; animated?: boolean }> = ({ color, label, animated }) => (
  <div className="flex items-center space-x-2">
    <div
      className="w-6 h-0.5"
      style={{
        backgroundImage: `repeating-linear-gradient(to right, ${color} 0px, ${color} 6px, transparent 6px, transparent 10px)`,
        ...(animated ? { animation: 'dash 0.6s linear infinite' } : {})
      }}
    />
    <span className="text-xs text-semantic-text-secondary">{label}</span>
  </div>
);

export const GraphLegend: React.FC<GraphLegendProps> = ({ cluster = false, inconsistentCount }) => {
  return (
    <div className="absolute bottom-4 right-4 z-20">
      <h4 className="text-sm font-semibold text-semantic-text-primary mb-2">Legend</h4>
      <div className="space-y-1">
        {/* Path status (all modes): pink = best path, grey = alternate path (CTX-106). */}
        <LegendRow color={BGP_EDGE_COLORS.best} label="Best Path" animated />
        <LegendRow color={BGP_EDGE_COLORS.alt} label="Alternate Path" />
        {cluster && (
          <>
            {/* Inconsistency is a BADGE colour (the `×N` count), not a line colour — show it as a
                small count-pill swatch so the legend matches how it renders on the edge. */}
            <div className="flex items-center space-x-2">
              <span
                className="inline-flex items-center rounded px-1 text-[10px] font-semibold leading-none"
                style={{ color: BGP_EDGE_COLORS.inconsistent }}
              >
                ×N
              </span>
              <span className="text-xs text-semantic-text-secondary">Count mismatch between reflectors</span>
            </div>
            {typeof inconsistentCount === 'number' && (
              <div className="pt-1 text-xs text-semantic-text-tertiary">
                {inconsistentCount} route {inconsistentCount === 1 ? 'table' : 'tables'} to review
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
